import { request as sonataRequest } from 'sonata-react-components';

import type { PaginationRequestParams, PaginationResult } from 'sonata-static-storage/lib/common/common.types';
import type { LineItem } from 'sonata-static-storage/lib/entities';
import type { UserInterface } from 'sonata-static-storage/lib/schemas/user/user.schema';

type Filters = {
  ids?: LineItem['_id'][];
  includeDeleted?: boolean;
  /** Only the line items of these insertion orders (comma-separated ids). */
  insertionOrders?: string;
  /** With `fields`, the items carry only `_id` plus those fields (no enrichment). */
  fieldsOnly?: boolean;
};

export type Params = Filters & PaginationRequestParams;

export function getLineItems(
  userId: UserInterface['_id'],
  params?: Params
): Promise<PaginationResult<LineItem & { deleted?: boolean }>> {
  return sonataRequest.makeRequest(`${taptap.config.backendUrlBase}multiflight/lineItems/${userId}`, {
    method: 'GET',
    params: new URLSearchParams(Object.entries(params ?? {}).map(([key, value]) => [key, value as string]))
  });
}

export type DeviatedLineItemsRequestBody = {
  accounts?: string[];
  dateFrom?: string;
  dateTo?: string;
  deviation?: number;
  filters?: Record<string, string[]>;
  itemsPerPage?: number;
  name?: string;
  page?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export type DeviatedLineItem = LineItem & {
  account: { id: string; name: string; currencyCountry?: string | null };
  // ponytail: the sync counters aren't part of the shared LineItem type yet; widen locally like extraData elsewhere.
  /** @deprecated lifetime total, replaced by syncRetries (MTF-772); fallback until the API drops it */
  bidderFailCounter?: number;
  /** Consecutive failed bidder syncs, reset to 0 on success */
  syncRetries?: number;
  deviationData: {
    expectedMonth: number;
    expectedMonthUser: number;
    expectedToday: number;
    expectedTodayUser: number;
    forecast: number;
    forecastDeviationPercentage: number | null;
    forecastToday: number;
    forecastTodayUser: number;
    forecastUser: number;
    lastUpdate: string;
    monthDeviationPercentage: number | null;
    todayDeviationPercentage: number | null;
    totalSpend: number;
    totalSpentUser: number;
  };
  metrics: {
    atr: number | null;
    audioComplete: number | null;
    clicks: number | null;
    ctr: number | null;
    dataFeeTotalUser: number | null;
    footfallAttribution: number | null;
    grossMargin: number | null;
    impressions: number | null;
    mediaFeeUser: number | null;
    playouts: number | null;
    storeVisitRate: number | null;
    totalGrossMargin: number | null;
    totalSpendUser: number | null;
    videoComplete: number | null;
    visit: number | null;
    vtr: number | null;
    webVisitRate: number | null;
  };
  opRisk: {
    daysLeft: number | null;
    level: 'high' | 'low' | 'medium';
    pacingStatus: string;
  };
  trader?: { id: string; name: string; email?: string } | null;
};

export type DeviatedLineItemsCountRequestBody = Omit<
  DeviatedLineItemsRequestBody,
  'itemsPerPage' | 'page' | 'sortBy' | 'sortOrder'
>;

export function getDeviatedLineItems(
  userId: UserInterface['_id'],
  body?: DeviatedLineItemsRequestBody,
  signal?: AbortSignal
): Promise<PaginationResult<DeviatedLineItem>> {
  return sonataRequest.makeRequest(`${taptap.config.backendUrlBase}multiflight/lineItems/${userId}/deviation`, {
    data: body,
    method: 'POST',
    signal
  });
}

export function getLineItem(userId: UserInterface['_id'], lineItemId: LineItem['_id']): Promise<LineItem> {
  return sonataRequest.makeRequest(`${taptap.config.backendUrlBase}multiflight/lineItems/${userId}/${lineItemId}`, {
    method: 'GET'
  });
}

export type DeviatedLineItemsCountResponse = {
  // Aggregated (not averaged) across all surviving, non-paused line items: total month forecast vs expected month.
  deviationPercentage: number | null;
  // USD, aggregated across all surviving line items regardless of their own account currency.
  expectedForecastGap: number;
  totalItems: number;
};

export function getDeviatedLineItemsCount(
  userId: UserInterface['_id'],
  body?: DeviatedLineItemsCountRequestBody,
  signal?: AbortSignal
): Promise<DeviatedLineItemsCountResponse> {
  return sonataRequest.makeRequest(`${taptap.config.backendUrlBase}multiflight/lineItems/${userId}/deviation/count`, {
    data: body,
    method: 'POST',
    signal
  });
}

export function diagnoseLineItem(
  userId: UserInterface['_id'],
  lineItemId: LineItem['_id']
): Promise<{ chatId: string; message: string }> {
  return sonataRequest.makeRequest(
    `${taptap.config.backendUrlBase}multiflight/lineItems/${userId}/${lineItemId}/diagnose`,
    { method: 'POST' }
  );
}
